// EditorHost — the material-content editing surface's impure shell
// (strangler-fig P3).
//
// Moved out of AppShellHost verbatim (.claude/analysis/ui-ddd-decomposition.md
// §6 Phase 3): owns the loaded material's edit state (elements/contentHash/
// pathById/materialProvenance/materialError), the compose wear (`loop`),
// undo/redo history, the review queue (proposals/reviewPendingId), the
// slash-menu's live state, and every structural-edit handler.
//
// SHAPE NOTE (same reasoning as OmnibarHost, Phase 2): this is a HOOK
// (`useEditorHost`), not a mounted `<Component/>`. `<MaterialDocument>` /
// `<MaterialToolbar>` / `<ProposalReview>` / `<Palette>` render deeply
// interleaved with shell-owned chrome (the dock, the tab strip, the rail
// overflow menu) inside AppShellHost's own render functions — there is no
// self-contained subtree to mount. AppShellHost keeps rendering that JSX,
// reading every editor-owned value/handler off this hook's returned API
// object instead of a bare local variable.
//
// CROSS-CLUSTER SEAMS (both deliberate, both documented where they're used):
//   - Quality/policy stay OUTSIDE this hook (Quality's own cluster, not yet
//     extracted — a future QualityHost owns them). Every content-changing
//     edit calls `onQualityStale()` instead of re-evaluating quality itself,
//     so quality ownership can move without EditorHost ever holding a
//     quality/policy prop. This REPLACES the old `refreshQuality(materialId)`
//     helper (now `AppShellHost`'s zero-arg `onEditorQualityStale`).
//   - The material-load fan-out (5 parallel port calls in one Promise.all)
//     stays SHELL-OWNED (per the extraction plan's explicit recommendation —
//     less churn, keeps the single Promise.all). The shell calls this hook's
//     `resetForMaterialSwitch`/`applyLoadedMaterial`/`setMaterialError`/
//     `setProposals` to hand the load's results down, instead of writing
//     `elements`/`proposals`/etc. state that no longer lives in the shell.
//   - §4b (Chat → Editor): a landed proposal from a compose turn is applied
//     via `applyLandedProposal(materialId, landed)`, which returns whether
//     THIS hook instance owns that material right now (`materialId ===
//     activeMaterialId`) — false means the shell must hold the proposal in
//     its own "pending by material" read-model instead of losing it. Chat's
//     own extraction (a future ChatHost) doesn't exist yet; `submitText`
//     stays in AppShellHost and calls this instead of reaching into `loop`
//     directly.

import { exhaustSwitchCase, Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { useRef, useState } from "react";
import {
    type ComposeLoop,
    emptyLoop,
    loopAfterReview,
    loopAfterSubmit,
} from "../../app/composeLoop.js";
import type { ExternalError } from "../../app/externalError.js";
import type {
    LandedProposal,
    LoadedMaterial,
    MaterialProvenance,
} from "../../app/ports.js";
import {
    anchorForMove,
    applyAcceptedProposal,
    applyEdit,
} from "../Editor/edit.js";
import type { RuleEffect } from "../Editor/inputRules.js";
import type { InsertIntent } from "../Editor/Palette/Palette.js";
import type { LoadedProposal } from "../Editor/ProposalReview/ProposalReview.js";
import {
    resolveSlashItems,
    type SlashItem,
} from "../Editor/SlashMenu/slashItems.js";
import type {
    Element,
    ElementId,
    ElementKind,
    HeadingElement,
    ReviewOutcome,
} from "../Editor/types.js";
import { mkChoiceEdits } from "./choiceEdits.js";
import {
    ACCEPT_OUTCOMES,
    atPaths,
    childIdsOf,
    editorShape,
    fieldValueOf,
    type LinePlace,
    samePath,
    type UnsavedLine,
    withField,
} from "./edits.js";
import {
    type FieldSave,
    fieldValue,
    hasDraft,
    isEditable,
    isQuiet,
} from "./saveQueue.js";
import type { EditorHostApi, EditorHostProps } from "./types.js";
import { type StructuralRun, useSaveQueue } from "./useSaveQueue.js";
import { type SlashPick, useSlashMenu } from "./useSlashMenu.js";
import { useUndoHistory } from "./useUndoHistory.js";

export type { EditorHostApi, EditorHostProps } from "./types.js";

// A fresh id for a locally made element (the impure edge): unique across
// sessions, since a page keeps the ids of blocks written in an earlier one
// (a per-session counter minted "new-0" again and the server refused it).
const mintElementId = (): ElementId => `new-${globalThis.crypto.randomUUID()}`;

export const useEditorHost = (props: EditorHostProps): EditorHostApi => {
    const {
        ports,
        activeMaterialId,
        dispatch,
        onQualityStale,
        showNotice,
        closeOverlay,
        surfaceSlash,
    } = props;
    const { t, i18n } = useLingui();

    const [elements, setElements] = useState<readonly Element[] | null>(null);
    // Every save goes through one ordered queue per material (saveQueue.ts):
    // one in flight, each guarded by the hash the previous one returned. Its
    // lane holds the optimistic-lock guard (seeded on load) and the id→path
    // map an edit's target is looked up in.
    const activeRef = useRef(activeMaterialId);
    activeRef.current = activeMaterialId;
    // The block whose "/" menu or Ask line is in progress (its draft waits).
    const heldRef = useRef<ElementId | undefined>(undefined);
    const saves = useSaveQueue({
        ports,
        activeMaterialId,
        held: () => heldRef.current,
        onSaved: onQualityStale,
        onRefused: () => showNotice({ type: "edit-failed" }),
        onConflict: (materialId, elementId) =>
            showNotice({ type: "edit-conflict", materialId, elementId }),
        onShowText: (materialId, id, text) => {
            if (activeRef.current !== materialId) return;
            setElements((els) =>
                els === null ? els : withField(els, { type: "text", id, text }),
            );
        },
    });
    const contentHash = saves.lane.hash;
    const pathById = saves.lane.paths;
    // The blank page's first paragraph, shown but not saved yet.
    const unsaved = useRef<UnsavedLine | undefined>(undefined);
    // Whether an edit to `id` can be saved. The first edit to the blank
    // page's unsaved paragraph saves the paragraph first (its edit waits
    // behind that insert).
    const editable = (id: ElementId): boolean => {
        const first = unsaved.current;
        if (
            first === undefined ||
            first.block.id !== id ||
            first.materialId !== activeMaterialId ||
            elements === null
        )
            return isEditable(saves.lane, id);
        unsaved.current = undefined;
        persistAppend(
            elements,
            first.block,
            elements.filter((e) => e.id !== id),
            first.at,
        );
        return true;
    };
    // The material whose content is shown (null: none yet, or a switch).
    const shownFor = useRef<string | null>(null);
    const [materialError, setMaterialError] = useState<ExternalError | null>(
        null,
    );
    // The active material's origin ("Created from" + creation moment) —
    // loaded with the content, reset on every material switch.
    const [materialProvenance, setMaterialProvenance] =
        useState<MaterialProvenance | null>(null);
    const [loop, setLoop] = useState<ComposeLoop>(emptyLoop);
    // Undo and Redo, saved like any edit (useUndoHistory.ts).
    const history = useUndoHistory({
        ports,
        activeMaterialId,
        isShown: (materialId) => activeRef.current === materialId,
        elements,
        setElements,
        setLoop,
        queue: saves.dispatch,
        dispatch,
        showNotice,
    });
    // The review queue: PENDING agent-edit proposals for the active material,
    // and the id of the one whose accept/reject is in flight (disables its
    // buttons). Refreshed on material switch and after each decision.
    const [proposals, setProposals] = useState<readonly LoadedProposal[]>([]);
    const [reviewPendingId, setReviewPendingId] = useState<string | null>(null);

    // Take a refused structural edit back: restore the pre-edit tree and drop
    // the undo entry the edit pushed, while that material is still the one
    // shown (the queue's runner shows the refusal).
    const undoTo =
        (materialId: string, prevElements: readonly Element[]) => () => {
            if (activeRef.current !== materialId) return;
            setElements(prevElements);
            history.dropNewest();
        };

    // A fresh block element of the chosen kind, with sensible defaults. Ids are
    // minted here (the impure edge); edit.ts stays pure.
    const mkBlock = (kind: ElementKind, id: ElementId): Element => {
        switch (kind) {
            case "heading":
                return { type: "heading", id, level: 2, text: t`New heading` };
            case "prose":
                return { type: "prose", id, markdown: t`New paragraph.` };
            case "objective":
                return { type: "objective", id, text: t`New objective` };
            // A note starts empty: its placeholder says what it takes.
            case "note":
                return { type: "note", id, text: "" };
            // An empty question (pen "Always writing", every case): its
            // stem to write, and one empty answer A, the right one, to go on
            // in with Enter.
            case "mcq":
                return {
                    type: "mcq",
                    id,
                    stem: "",
                    answerMode: "single",
                    choices: [{ id: `${id}-a`, text: "", correct: true }],
                };
            default:
                return exhaustSwitchCase(kind);
        }
    };

    // The element whose primary EditableText should auto-enter edit mode +
    // take focus the moment it next mounts (Bug 1 fix, 2026-08-19): every
    // insert/retype dispatch below sets this to the new/retyped block's id
    // so the author keeps typing seamlessly instead of the caret dropping
    // to nowhere. Cleared once that EditableText reports the signal
    // consumed (its own one-shot contract — see types.ts).
    const [autoEditElementId, setAutoEditElementId] =
        useState<ElementId | null>(null);
    const onAutoEditConsumed = () => setAutoEditElementId(null);

    // Insert from the palette: media is the deferred wiring slice (a notice); a
    // block is minted, appended OPTIMISTICALLY to the local AST, then PERSISTED
    // through the same `insert_child_element` primitive the agent uses. On
    // success the block's domain path is threaded into `pathById` (so it becomes
    // immediately text-editable) and the new content hash swapped in; on failure
    // the optimistic insert is rolled back and the refusal surfaced. Without this
    // persistence the insert vanished on reload (the P23 E1 break).
    const onInsert = (intent: InsertIntent) => {
        if (elements === null) {
            closeOverlay();
            return;
        }
        if (intent.type === "media") {
            showNotice({ type: "media" });
            closeOverlay();
            return;
        }
        const id: ElementId = mintElementId();
        appendBlock(mkBlock(intent.kind, id));
        closeOverlay();
    };

    // An EMPTY paragraph, the caret in it: at the end (a blank or planned
    // page, the writing line, #317) or the start (above a first part). Only
    // shown: the first edit in it saves it (see `editable`), so a click
    // alone changes nothing and a planned page stays planned.
    const startWriting = (at: LinePlace) => {
        if (activeMaterialId === null || elements === null) return;
        const block: Element = {
            type: "prose",
            id: mintElementId(),
            markdown: "",
        };
        unsaved.current = { materialId: activeMaterialId, block, at };
        setElements(
            at === "first" ? [block, ...elements] : [...elements, block],
        );
        setAutoEditElementId(block.id);
    };
    const onStartWriting = () => startWriting("last");
    const onWriteAbove = () => startWriting("first");

    // Append `block` OPTIMISTICALLY, the caret in it, then persist it.
    const appendBlock = (block: Element) => {
        if (elements === null) return;
        persistAppend(
            applyEdit(elements)({
                type: "insert-element",
                element: block,
                index: elements.length,
            }),
            block,
            elements,
            "last",
        );
        setAutoEditElementId(block.id);
    };
    const persistAppend = (
        next: readonly Element[],
        block: Element,
        base: readonly Element[],
        at: LinePlace,
    ) =>
        runStructuralEdit(
            next,
            atPaths([], (materialId, hash) =>
                at === "first"
                    ? ports.insertFirstBlock(materialId, block, hash)
                    : ports.insertBlock(materialId, block, hash),
            ),
            block.id,
            base,
        );

    // A live markdown input-rule (`# `/`## `/`### `) fired while typing a
    // prose block. `deferred` names a rule that matched but has no Element
    // kind to land in yet (bullet/numbered lists — inputRules.ts's stated
    // MVP shortcut) — surfaced as the same "can't do that yet" notice the
    // palette's media row uses, never silently dropped. A `retype` effect
    // applies OPTIMISTICALLY (the pure `retype-element` fold — SAME id, only
    // `type`/`text`/`attrs` change) then PERSISTS through the domain's
    // `retype_element` primitive; on failure it rolls back exactly like
    // `onEditText`. `id` is the block the rule fired in (MaterialDocument's
    // per-element wrapper); `effect.command.id` names the same block — using
    // the explicit param keeps this in step with the other by-id handlers.
    const onInputRule = (id: ElementId, effect: RuleEffect) => {
        if (effect.type === "deferred") {
            showNotice({ type: "edit-failed" });
            return;
        }
        if (activeMaterialId === null || elements === null) return;
        if (!editable(id)) return;
        const to = effect.command.to;
        // The retyped block mounts a BRAND-NEW EditableText (a different
        // renderer takes over the same id) — keep the author typing into it.
        setAutoEditElementId(id);
        runStructuralEdit(
            applyEdit(elements)({ type: "retype-element", id, to }),
            atPaths([id], (materialId, hash, pathOf) =>
                ports.retypeElement(materialId, pathOf(id), to, hash),
            ),
        );
    };

    // A slash-menu row was picked: mint a fresh block of the chosen kind and
    // insert it AFTER the tracked block — OPTIMISTICALLY (the pure
    // `insert-element` fold), then PERSISTED through the domain's sibling-
    // relative `insert_element` primitive (`insertBlockAfter`, the twin of
    // `onInsert`'s `insertBlock` which always appends at the document's
    // end). Mirrors `onInsert`'s rollback + `pathById` threading exactly, so
    // the fresh block becomes immediately text-editable. Media rows are the
    // same deferred slice `onInsert` already surfaces.
    const commitSlash = (
        item: SlashItem,
        anchorId: ElementId,
        words: string,
        how: SlashPick,
    ) => {
        // A surface row: the surface acts (the Studio's Create); the "/" was
        // a command, not content.
        if (item.intent.type === "surface") {
            surfaceSlash?.onPick({
                at: item.intent.at,
                anchor: anchorId,
                words,
                how,
            });
            return;
        }
        if (elements === null) return;
        if (item.intent.type === "media") {
            showNotice({ type: "media" });
            return;
        }
        if (!editable(anchorId)) return; // not path-editable ⇒ no-op
        const id: ElementId = mintElementId();
        // A heading chip: a heading at its level.
        const block: Element =
            item.intent.type === "heading"
                ? {
                      type: "heading",
                      id,
                      level: item.intent.level,
                      text: t`New heading`,
                  }
                : mkBlock(item.intent.kind, id);
        const anchorIndex = elements.findIndex((e) => e.id === anchorId);
        const at = anchorIndex === -1 ? elements.length : anchorIndex + 1;
        runStructuralEdit(
            applyEdit(elements)({
                type: "insert-element",
                element: block,
                index: at,
            }),
            atPaths([anchorId], (materialId, hash, pathOf) =>
                ports.insertBlockAfter(
                    materialId,
                    pathOf(anchorId),
                    block,
                    hash,
                ),
            ),
            id,
        );
        setAutoEditElementId(id);
    };

    // Enter at the END of a top-level text block ⇒ a new prose block below (the
    // document-first "new line is a new block" gesture; a true mid-text split is
    // deferred, document-first-editor.md §4). The rented caret hands up the text
    // it just committed, so this queues TWO saves in order: the anchor block's
    // text (when there is any to save) THEN the sibling insert, each sent with
    // the hash the one before returned. The text is kept even if the insert is
    // refused (only the new block goes). An anchor with no path (pqbl
    // course-object content) is not path-editable ⇒ no-op; one whose own
    // insert is still on its way is, and waits for it.
    const onEnterBlockBelow = (id: ElementId, committedText: string) => {
        if (activeMaterialId === null || elements === null) return;
        if (!editable(id)) return; // not path-editable ⇒ no-op
        const afterText = commitField({
            type: "text",
            id,
            text: committedText,
        });
        if (afterText === undefined) return;
        const newId: ElementId = mintElementId();
        // A fresh EMPTY paragraph — the author keeps typing into it (autoEdit),
        // exactly the Notion "Enter drops you into a new block" feel.
        const block: Element = { type: "prose", id: newId, markdown: "" };
        const anchorIndex = afterText.findIndex((e) => e.id === id);
        const at = anchorIndex === -1 ? afterText.length : anchorIndex + 1;
        runStructuralEdit(
            applyEdit(afterText)({
                type: "insert-element",
                element: block,
                index: at,
            }),
            atPaths([id], (materialId, hash, pathOf) =>
                ports.insertBlockAfter(materialId, pathOf(id), block, hash),
            ),
            newId,
            afterText,
        );
        setAutoEditElementId(newId);
    };

    // Backspace on an EMPTY top-level block ⇒ remove it and focus the previous
    // (the document-first "delete an empty line" gesture; a text-MERGE into the
    // previous block is deferred, §4). Only when there IS a previous block —
    // Backspace in the first/only block is a no-op, so the document can never be
    // emptied by this gesture. Reuses the `remove_element` primitive + the shared
    // rollback runner; the previous block gets the autoEdit focus signal.
    const onDeleteEmptyBlock = (id: ElementId) => {
        if (elements === null) return;
        const index = elements.findIndex((e) => e.id === id);
        if (index <= 0) return; // first/only block ⇒ nothing to merge into
        if (!editable(id)) return; // not path-editable ⇒ no-op
        // The caret goes to the text above (a part has none).
        const text = elements.slice(0, index).filter((e) => e.type !== "part");
        const prev = text.at(-1);
        runStructuralEdit(
            applyEdit(elements)({ type: "remove-element", id }),
            atPaths([id], (materialId, hash, pathOf) =>
                ports.removeElement(materialId, pathOf(id), hash),
            ),
        );
        if (prev !== undefined) setAutoEditElementId(prev.id);
    };

    // Resolve an in-editor review at an element. If a REAL pending proposal
    // backs this element (matched by domain path against the loaded proposal
    // queue), the decision goes through the SAME persisting commands the review
    // pane uses — accept applies + records the acceptance, reject discards — so
    // the resolution survives a reload (previously this path called a port that
    // reported success while persisting NOTHING). Only the mock's landed-turn
    // wear (loop.annotations; the real turn lands none) has no backing proposal:
    // there we apply the optimistic AST transform for the design preview and
    // clear the wear, claiming no persistence.
    const onReview = (elementId: ElementId, outcome: ReviewOutcome) => {
        if (activeMaterialId === null || elements === null) return;
        const path = pathById.get(elementId);
        const backing =
            path !== undefined
                ? proposals.find((p) => samePath(p.path, path))
                : undefined;
        if (backing !== undefined) {
            if (ACCEPT_OUTCOMES.has(outcome.type))
                onAcceptProposal(backing.proposalId);
            else onRejectProposal(backing.proposalId);
            return;
        }
        const proposal = loop.annotations.get(elementId)?.proposal;
        const next =
            proposal === undefined
                ? elements
                : applyAcceptedProposal(elements, elementId, proposal, outcome);
        const nextLoop = loopAfterReview(loop, elementId);
        history.record({ elements, loop }, { elements: next, loop: nextLoop });
        setElements(next);
        setLoop(nextLoop);
    };

    // Approve a pending agent proposal: the server applies its patch to content
    // (the SAME edit primitive a manual edit uses) and records acceptance. On
    // success the material has CHANGED, so reload its content + the queue, and
    // signal quality is stale (Quality's own cluster re-evaluates — see the
    // "CROSS-CLUSTER SEAMS" note; the original combined content+quality+policy
    // Promise.all is split here so EditorHost never holds a quality/policy
    // setter). Distinct from reject — its own command
    // (`/commands/acceptProposal`).
    const onAcceptProposal = (proposalId: string) => {
        if (activeMaterialId === null) return;
        setReviewPendingId(proposalId);
        const run = async () => {
            const r = await ports.acceptProposal(proposalId);
            if (Result.isErr(r)) {
                // The author changed the text since it was suggested: theirs
                // stays, and the notice says why.
                showNotice(
                    r.error._tag === "External.Refused" &&
                        r.error.errorType === "proposal_base_changed"
                        ? { type: "suggestion-outdated" }
                        : { type: "edit-failed" },
                );
                setReviewPendingId(null);
                return;
            }
            const [contentR, proposalsR] = await Promise.all([
                ports.loadMaterial(activeMaterialId),
                ports.loadProposals(activeMaterialId),
            ]);
            if (Result.isOk(contentR)) {
                setElements(contentR.value.elements);
                saves.dispatch({
                    type: "loaded",
                    materialId: activeMaterialId,
                    hash: contentR.value.contentHash,
                    paths: contentR.value.pathById,
                });
                history.clear();
            }
            setProposals(
                Result.isOk(proposalsR)
                    ? proposalsR.value
                    : proposals.filter((p) => p.proposalId !== proposalId),
            );
            setReviewPendingId(null);
            onQualityStale();
        };
        void run();
    };

    // Reject a pending agent proposal: discard it, no content change
    // (`/commands/rejectProposal`). Drop it from the queue optimistically.
    const onRejectProposal = (proposalId: string) => {
        if (activeMaterialId === null) return;
        setReviewPendingId(proposalId);
        const run = async () => {
            const r = await ports.rejectProposal(proposalId);
            if (Result.isErr(r)) {
                showNotice({ type: "edit-failed" });
                setReviewPendingId(null);
                return;
            }
            setProposals((ps) => ps.filter((p) => p.proposalId !== proposalId));
            setReviewPendingId(null);
        };
        void run();
    };

    // A field's text is done (blur, Enter, Esc): show it, and queue it behind
    // every earlier save (with the drafts typed before it). Text equal to what
    // is shown still flushes a waiting draft of the field (a refused save is
    // retried this way). Returns the tree as now shown (undefined: the field
    // is not path-editable, e.g. a pqbl material's synthesised id, a no-op).
    const commitField = (field: FieldSave): readonly Element[] | undefined => {
        if (activeMaterialId === null || elements === null) return undefined;
        const changed = fieldValueOf(elements, field) !== fieldValue(field);
        if (!changed && !hasDraft(saves.lane, field)) return elements;
        if (!editable(field.id)) return undefined;
        const next = changed ? withField(elements, field) : elements;
        if (changed) {
            dispatch({ type: "resource-edited", id: activeMaterialId });
            history.record({ elements, loop }, { elements: next, loop });
            setElements(next);
        }
        saves.dispatch({
            type: "committed",
            materialId: activeMaterialId,
            save: field,
            shown: fieldValueOf(elements, field),
        });
        return next;
    };
    // Text still being typed: saved once the author pauses (saveQueue.ts).
    const draftField = (field: FieldSave) => {
        if (activeMaterialId === null || !editable(field.id)) return;
        saves.dispatch({
            type: "drafted",
            materialId: activeMaterialId,
            save: field,
            shown:
                elements === null ? undefined : fieldValueOf(elements, field),
        });
    };
    const keepMine = (materialId: string, id: ElementId) =>
        saves.dispatch({ type: "kept-mine", materialId, id });
    const takeTheirs = (materialId: string, id: ElementId) =>
        saves.dispatch({ type: "took-theirs", materialId, id });

    // Manual inline text edit at an element (or a choice), persisted through
    // the SAME `update_element_text` primitive the agent uses; feedback (its
    // twin) through `update_element_attrs` (`{ feedback }`).
    const onEditText = (id: ElementId, text: string) => {
        commitField({ type: "text", id, text });
    };
    const onEditFeedback = (id: ElementId, feedback: string) => {
        commitField({ type: "feedback", id, feedback });
    };
    const onDraftText = (id: ElementId, text: string) =>
        draftField({ type: "text", id, text });
    const onDraftFeedback = (id: ElementId, feedback: string) =>
        draftField({ type: "feedback", id, feedback });

    // The shared optimistic-persist runner behind every structural edit (insert,
    // move / remove a block, add / remove / reorder a choice, a heading level or
    // answer mode, retype): snapshot `base` for undo, show `next`, and queue
    // `persist` behind every earlier save of the material, where it is sent
    // with the hash the save before it returned. A refusal takes `next` back
    // (to `base`); `mints` names the element it creates, whose path its
    // success threads in so later edits to it can be sent.
    const runStructuralEdit = (
        next: readonly Element[],
        persist: StructuralRun["persist"],
        mints?: ElementId,
        base: readonly Element[] | null = elements,
    ) => {
        if (activeMaterialId === null || base === null) return;
        const materialId = activeMaterialId;
        dispatch({ type: "resource-edited", id: materialId });
        history.record({ elements: base, loop }, { elements: next, loop });
        setElements(next);
        saves.dispatch({
            type: "committed",
            materialId,
            save: {
                type: "structural",
                run: {
                    persist,
                    refused: () => {
                        undoTo(materialId, base)();
                        showNotice({ type: "edit-failed" });
                    },
                },
                mints,
                within: childIdsOf(next, mints),
            },
        });
    };

    // A question's answers: add, remove, move, the right one, the mode
    // (choiceEdits.ts).
    const choices = mkChoiceEdits({
        ports,
        elements,
        editable,
        runStructuralEdit,
        mintId: mintElementId,
        commitField,
        focus: setAutoEditElementId,
    });

    // Move a block to `toIndex` (index in the list with the block removed). The
    // domain move primitive is sibling-relative, so translate the index into an
    // anchor + before/after with `anchorForMove`, then persist through the same
    // `move_element` the agent uses. A one-block list (no anchor) or an
    // unresolvable path (pqbl course-object content) is a no-op.
    const onMoveElement = (id: ElementId, toIndex: number) => {
        if (elements === null) return;
        const anchor = anchorForMove(elements, id, toIndex);
        if (anchor === undefined) return;
        if (!editable(id) || !editable(anchor.anchorId)) return;
        runStructuralEdit(
            applyEdit(elements)({ type: "move-element", id, toIndex }),
            atPaths([id, anchor.anchorId], (materialId, hash, pathOf) =>
                ports.moveElement(
                    materialId,
                    pathOf(id),
                    pathOf(anchor.anchorId),
                    anchor.position,
                    hash,
                ),
            ),
        );
    };

    // Remove a block through the `remove_element` primitive. Unresolvable path ⇒
    // no-op (mirrors the text handlers).
    const onRemoveElement = (id: ElementId) => {
        if (elements === null || !editable(id)) return;
        runStructuralEdit(
            applyEdit(elements)({ type: "remove-element", id }),
            atPaths([id], (materialId, hash, pathOf) =>
                ports.removeElement(materialId, pathOf(id), hash),
            ),
        );
    };

    // Set a heading's level through the merge-safe `patch_element_attrs`
    // (`{ level }`) so it never clobbers other heading attrs. Unresolvable path ⇒
    // no-op.
    const onSetHeadingLevel = (
        id: ElementId,
        level: HeadingElement["level"],
    ) => {
        if (elements === null || !editable(id)) return;
        runStructuralEdit(
            applyEdit(elements)({ type: "set-heading-level", id, level }),
            atPaths([id], (materialId, hash, pathOf) =>
                ports.patchElementAttrs(
                    materialId,
                    pathOf(id),
                    { level },
                    hash,
                ),
            ),
        );
    };

    // The "/" menu (useSlashMenu.ts): the surface's rows (the Studio's
    // Create) and the template's insert allow-list (the SAME `editorShape`
    // the toolbar's Palette reads, as much as the surface keeps), in the
    // Create menu's groups, live-filtered by the typed query.
    const slash = useSlashMenu(
        [
            ...(surfaceSlash?.items ?? []),
            ...resolveSlashItems(
                editorShape,
                i18n,
                surfaceSlash?.keep ?? "all",
            ),
        ],
        commitSlash,
        surfaceSlash,
        // Words left in a line that asked join the text kept before its "/"
        // (pen Q73, mid-line), never replace it.
        (id, words) => {
            const block = elements?.find((e) => e.id === id);
            const before =
                block?.type === "prose" ? block.markdown.trimEnd() : "";
            onEditText(id, before === "" ? words : `${before} ${words}`);
        },
    );
    // While a block's "/" menu is open, or it is the line that asks, its
    // draft is not saved on a pause.
    heldRef.current =
        slash.slashTrigger?.state === "open"
            ? slash.slashTrigger.forElementId
            : (surfaceSlash?.asking?.at ?? surfaceSlash?.heldAt);

    // === Material-load wiring (called by the shell's fan-out effect) =========
    // The fan-out itself (loadMaterial/loadConversation/evaluateMaterial/
    // evaluateMaterialPolicy/loadProposals in one Promise.all) stays
    // shell-owned (.claude/analysis/ui-ddd-decomposition.md §6 Phase 3) — these
    // just receive its results for the pieces EditorHost owns.

    // On a genuine material switch, clear every editor-owned piece (quality/
    // policy/conversation are the shell's own resets, called alongside this).
    // The material's saves still waiting go on in their own lane.
    const resetForMaterialSwitch = () => {
        shownFor.current = null;
        unsaved.current = undefined;
        setElements(null);
        setMaterialProvenance(null);
        setLoop(emptyLoop);
        history.clear();
        setProposals([]);
    };
    // A re-read of the same content (the author's own edit came back as a
    // new version) changes nothing, so the block being typed in keeps its
    // caret; only content written elsewhere replaces what is shown. A read
    // that lands while this material's saves are on their way may predate
    // them, so it is left out too (the last save's own new version reads
    // again).
    const applyLoadedMaterial = (loaded: LoadedMaterial) => {
        if (activeMaterialId === null) return;
        const lane = saves.laneNow(activeMaterialId);
        if (
            shownFor.current === activeMaterialId &&
            (loaded.contentHash === lane.hash || !isQuiet(lane))
        )
            return;
        shownFor.current = activeMaterialId;
        unsaved.current = undefined;
        saves.dispatch({
            type: "loaded",
            materialId: activeMaterialId,
            hash: loaded.contentHash,
            paths: loaded.pathById,
        });
        setElements(loaded.elements);
        setMaterialProvenance(loaded.provenance);
    };

    // §4b: fold a compose turn's landed proposal into `loop`, snapshotting for
    // undo — but only when THIS instance owns the target material right now
    // (it always does today, since `submitText`'s target falls back to
    // `activeMaterialId` first; the explicit check is what keeps this correct
    // once a real ChatHost can target an material whose editor isn't mounted).
    const applyLandedProposal = (
        materialId: string,
        landed: LandedProposal,
    ): boolean => {
        if (materialId !== activeMaterialId) return false;
        const nextLoop = loopAfterSubmit(
            loop,
            landed.targetId,
            landed.proposal,
        );
        if (elements !== null)
            history.record({ elements, loop }, { elements, loop: nextLoop });
        setLoop(nextLoop);
        return true;
    };

    return {
        elements,
        contentHash,
        pathById,
        materialError,
        materialProvenance,
        loop,
        canUndo: history.canUndo,
        canRedo: history.canRedo,
        proposals,
        reviewPendingId,
        resetForMaterialSwitch,
        applyLoadedMaterial,
        setMaterialError,
        setProposals,
        undo: history.undo,
        redo: history.redo,
        onEditText,
        onEditFeedback,
        onDraftText,
        onDraftFeedback,
        ...choices,
        onSetHeadingLevel,
        onMoveElement,
        onRemoveElement,
        onInputRule,
        onInsert,
        onStartWriting,
        onWriteAbove,
        onReview,
        onEnterBlockBelow,
        onDeleteEmptyBlock,
        onAcceptProposal,
        onRejectProposal,
        keepMine,
        takeTheirs,
        ...slash,
        autoEditElementId,
        onAutoEditConsumed,
        editorShape,
        applyLandedProposal,
    };
};
